<template>
  <div class="binding">
    <h1 class="hgroup heading1">
      {{ $t('Привяжи карту к своему аккаунту') }} {{ userAccount }}
    </h1>

    <section class="binding_selector" :class="secondStep && 'hidden-step'">
      <h2 class="binding_step">{{ $t('Шаг 1 из 2') }}</h2>
      <p class="binding_description">
        1. {{ $t('Выбери игру, для которой будешь получать бонусы') }}
      </p>
      <div class="binding_list">
        <div
          v-for="(title, game) in gamesList"
          :key="game"
          :class="['game-card', 'game-card__' + game, selectedGame === game && 'selected']"
          v-text="title.name"
          @click="$emit('selectGame', game)"
        />
      </div>
      <Button class="binding_next" @click="changeStep">
        {{ $t('Далее') }}
      </Button>
    </section>

    <section :class="['binding_form', secondStep || 'hidden-step']">
      <h2 class="binding_step">
        <span class="binding_back" @click="changeStep">Back icon</span>
        {{ $t('Шаг 2 из 2') }}
      </h2>
      <p class="binding_description">
        2. {{ $t('Введи номер участника программы') }}
      </p>

      <div :class="['card-form', cardInputError && 'error']">
        <div class="card-form_holder">
          <p class="card-form_label">
            {{ $t('Номер участника программы') }}
          </p>
          <div class="card-form_code">
           <input
            type="search"
            ref="participantNumberEl"
            v-model="participantNumber"
            name="participantNumber"
            class="card-form_input"
            placeholder="0000 0000 0000"
            autocomplete="off"
            @input="onInputCodeHandler"
          >
          </div>
          <p
            v-if="invalidCardError"
            class="card-form_error"
            v-html="messageByError"
            @click="onSupportLinkClick"
          />
        </div>
      </div>

      <Transition name="fade">
        <label
          :class="['checkbox', { error: termsError }, { hidden: !isShowTerms }]"
          :inert="!isShowTerms"
        >
          <input
            class="checkbox_hidden"
            type="checkbox"
            :checked="termsAgreed"
            @change="onTermsClick"
          />
          <span class="checkbox_flag"/>
          <span v-html="agreementText"/>
        </label>
      </Transition>
      <Button
        :class="bindButtonClasses"
        :waiting="loading"
        :disabled="!isCardNumberValid || !termsAgreed || loading"
        @click="onBindCard(!isCardNumberValid || !termsAgreed || loading)"
      >
        {{ $t('Привязать') }}
      </Button>
    </section>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'
import { Maskito } from '@maskito/core'
import { namespace } from 'vuex-class'
import pick from 'lodash/pick'
import debounce from 'lodash/debounce'

import { ValidationError } from '@/common/api/errors'
import {
  gameTitles,
  availableGames,
  VALIDATION_ERRORS,
  SUPPORT_PORTAL_LINK_URL,
  ACCOUNT_QUERY_KEY
} from '@/constants'
import { getRulesUrl } from '@/helpers'
import Button from '@/common/components/Button'
import Message from '@/common/components/Message'
import CobrandAPIClient from '@/common/api/cobrandApiClient'

const cards = namespace('cards')
const auth = namespace('auth')

const CODE_LENGTH = 12

const checkValidationError = (error) => {
  const properInstance = error instanceof ValidationError
  if (!properInstance) {
    return false
  }

  const [{ code }] = error.errors
  return properInstance && code
}

const inputDebounce = debounce((fn) => fn(), 1000)

@Component({
  components: { Button, Message }
})
class Binding extends Vue {
  @Prop({ type: String, required: true }) selectedGame

  @cards.Action bindCard
  @cards.State loading
  @auth.State(({ user }) => user.name) userAccount
  @auth.State(({ user }) => user.id) userId

  gamesList = pick(gameTitles, availableGames)
  get gamesCodeList () {
    return Object.keys(this.gamesList)
  }

  participantNumber = ''
  maskitoEl = undefined
  cardMaskOptions = {
    mask: [/[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, ' ', /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, ' ', /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/],
    postprocessors: [({ value, selection }) => ({ value: value.toUpperCase(), selection })]
  }

  secondStep = false

  get agreementText () {
    return this.$t('Я согласен с %(link_start)sПравилами программы%(link_stop)s', this.rulesLink)
  }
  termsAgreed = false

  termsError = false
  cardInputError = false
  invalidCardError = ''
  /**
   * Bank partner tag
   * @type {null|string}
   */
  tag = null
  valueFull = false

  mounted () {
    this.init()
  }

  beforeDestroy () {
    this.participantNumberElDestroy()
  }

  init () {
    this.fillForm()
    this.participantNumberElInit()
  }

  participantNumberElInit () {
    this.maskitoEl = new Maskito(this.$refs.participantNumberEl, this.cardMaskOptions)
  }

  participantNumberElDestroy () {
    if (this.maskitoEl) this.maskitoEl.destroy()
  }

  /**
   * Fill form when params are available from URL (on page load)
   */
  fillForm () {
    const params = this.$route.query

    const code = params.cb_code
    const game = params.game

    if (code && game) {
      this.participantNumber = code

      if (this.gamesCodeList.includes(game)) {
        this.$emit('selectGame', game)
      }
    }
  }

  changeStep () {
    this.secondStep = !this.secondStep
  }

  onTermsClick () {
    this.$analytics.trackBindPageTermsClick({ screenId: this.$route.name })
    this.termsAgreed = !this.termsAgreed
    this.termsError = false
  }

  validateCardForm () {
    this.termsError = !this.termsAgreed
    this.cardInputError = !this.isCardNumberValid
    if (this.cardInputError) {
      this.$analytics.trackCardFormValidationError({
        inputValues: [this.participantNumber],
        codeSectionLength: CODE_LENGTH
      })
    }

    return !(this.termsError || this.cardInputError)
  }

  resetCardErrors () {
    this.invalidCardError = ''
    this.cardInputError = false
  }

  async onBindCard () {
    const isFormValid = this.validateCardForm()
    if (isFormValid) {
      this.$analytics.trackBindCardConfirm({ screenId: this.$route.name })
      try {
        let responseTag = null
        await this.bindCard({
          game: this.selectedGame,
          cardCode: this.participantNumberNormalized,
          callback: ({ tag }) => {
            responseTag = tag
          }
        })
        this.$analytics.trackCardBindSuccess({ screenId: this.$route.name, game: this.selectedGame })
        this.$emit('showSuccessMessage', responseTag)
      } catch (error) {
        const validationError = checkValidationError(error)

        if (validationError) {
          this.invalidCardError = validationError
        } else {
          throw error
        }
      }
    }
  }

  get messageByError () {
    const bindingRulesLink = { link_start: `<a target="_blank" href="${SUPPORT_PORTAL_LINK_URL}">`, link_stop: `</a>` }

    const { ENTITY_NOT_FOUND, CODE_ALREADY_BOUND, CODE_ALREADY_USED, BANK_ACCOUNT_NOT_FOUND_BY_EAN } = VALIDATION_ERRORS

    switch (this.invalidCardError) {
      case ENTITY_NOT_FOUND:
        return this.$t(`Неверный бонусный номер. Повторите попытку или %(link_start)sобратитесь в службу поддержки%(link_stop)s.`, bindingRulesLink)
      case CODE_ALREADY_BOUND:
      case CODE_ALREADY_USED:
        return this.$t(`Данный бонусный номер уже зарегистрирован. Повторите попытку или %(link_start)sобратитесь в службу поддержки%(link_stop)s.`, bindingRulesLink)
      case BANK_ACCOUNT_NOT_FOUND_BY_EAN:
        return this.$t(`Данная карта заблокирована. Обратитесь в службу поддержки банка.`)
      default:
        return this.$t('Возникла ошибка. Повторите попытку.')
    }
  }

  get bindButtonClasses () {
    return {
      binding_button: true
    }
  }

  isCardNumberValid = false;
  isShowTerms = false

  // Not obvious link formatting
  // used for localization purpose
  get rulesLink () {
    return {
      link_start: `<a class="link" target="_blank"  href="${getRulesUrl(this.tag)}">`,
      link_stop: '</a>'
    }
  }

  get participantNumberNormalized () {
    return this.participantNumber.trim().replaceAll(' ', '')
  }

  /**
   * Validate codem, reset "terms" state and request card info if valid
   */
  onInputCodeHandler () {
    return inputDebounce(() => {
      this.resetCardErrors()
      this.isShowTerms = false
      this.termsAgreed = false

      this.isCardNumberValid = this.participantNumberNormalized.length === CODE_LENGTH && /^[A-Z0-9]+$/.test(this.participantNumberNormalized)

      if (this.isCardNumberValid) {
        this.getCardInfoByCardCode()
      }
    })
  }

  /**
   * Request card info by card code if valid
   */
  async getCardInfoByCardCode () {
    if (this.valueFull) return

    try {
      const result = await CobrandAPIClient.getInfoCard({
        [`filter[${ACCOUNT_QUERY_KEY}]`]: this.userId,
        'filter[card_code]': this.participantNumberNormalized
      })

      this.tag = result.tag || null
      this.isShowTerms = true
    } catch (error) {
      const validationError = checkValidationError(error)
      this.invalidCardError = validationError || 'DEFAULT_ERROR'
      this.tag = null
    }
  }

  onSupportLinkClick ($event) {
    const { href, localName } = $event.target

    // to check link has been clicked
    if (localName === 'a') {
      this.$analytics.trackSupportButtonClick({ screenId: this.$route.name, linkUrl: href })
    }
  }
}

export default Binding
</script>

<style lang="scss" src="./styles/binding.scss"></style>
<style lang="scss" src="./styles/card-form.scss"></style>
<style lang="scss" src="./styles/checkbox.scss"></style>
<style lang="scss" src="./styles/game-card.scss"></style>
<style lang="scss">
.hidden-step {
  display: none;

  @include media_tablet {
    display: block;
  }
}

.__disabled {
  pointer-events: none;
  opacity: .3;
}

</style>
